import { ADMIN_ORDER_STATUSES } from '@carcheck/shared';
import { z } from 'zod';

import type { StatusOption } from '../../../components/filters/StatusMultiSelect';

/**
 * URL filter schema for the orders list (AUT-123). Mirrors the backend
 * `ListAdminOrdersQuerySchema` — `status` is a multi-select built from the
 * 11-value `ADMIN_ORDER_STATUSES` (NOT hand-typed; CLAUDE.md rule #11/#7),
 * `q` is a min-3-char free-text search (ILIKE over orderNumber + customer
 * phone), `from` / `to` are ISO date bounds. Every key has a `.catch`
 * default so a malformed / absent query param degrades safely instead of
 * throwing — `useUrlFilters` parses the raw query string straight through.
 */

/** Min free-text search length (mirrors backend MIN_SEARCH_LENGTH). */
export const MIN_SEARCH_LENGTH = 3;

/** `status=paid,in_progress` → string[] of valid statuses (empty on junk). */
const statusSchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) =>
    (raw ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is (typeof ADMIN_ORDER_STATUSES)[number] =>
        (ADMIN_ORDER_STATUSES as readonly string[]).includes(s),
      ),
  );

const qSchema = z.string().optional().catch(undefined);

/** ISO date (YYYY-MM-DD) bound — opaque string, validated by the picker. */
const dateSchema = z.string().optional().catch(undefined);

export const orderFiltersSchema = z.object({
  status: statusSchema,
  q: qSchema,
  from: dateSchema,
  to: dateSchema,
});

export type OrderFilters = z.infer<typeof orderFiltersSchema>;

/** Status filter options (multi-select) — labels resolved via i18n keys. */
export const STATUS_OPTIONS: StatusOption[] = ADMIN_ORDER_STATUSES.map((value) => ({
  value,
  labelKey: `orders.status.${value}`,
}));

/** True when any managed filter is active (drives the Clear-filters link). */
export const hasActiveFilters = (filters: OrderFilters): boolean =>
  (filters.status?.length ?? 0) > 0 ||
  Boolean(filters.q && filters.q.length > 0) ||
  Boolean(filters.from) ||
  Boolean(filters.to);
